import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import debounce from 'lodash/debounce';
import {
  IAnalysisSummaryInfoFieldsFragment,
  IImageEmbedFieldsFragment,
  ScratchpadNoteType,
} from '../../../utils/apollo/graphqlSchema';
import { ImageEmbedUpload, ImageUploady } from '../../ImageUploady';
import { IRichTextEditorRef } from '../../RichTextEditor/RichTextEditor';
import { useScratchpadContext } from '../ScratchpadContext';
import { isNil } from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectOpenedModalType } from '../../../redux/selectors/modal';
import { ModalSliceType, openModal } from '../../../redux/slices/modal';
import ReportSearchModal from '../../ReportSearchModal';
import DashboardSearchModal from '../../DashboardSearchModal';
import CohortSearchModal from '../../CohortSearchModal/CohortSearchModal';
import { ContentSplitResult } from './useContentSplit';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableNote from './Note/SortableNote';
import DragHeader from './Note/DragHeader';
import { theme } from '../../../styles/theme';
import { IScratchPadNoteFragment } from '../types';
import useScratchpadImageEmbed from '../utils/useScratchpadImageEmbed';
import { MaybeNil } from '../../../utils/interfaces';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0; // Important for flex child scrolling
  padding: 16px;
  background-color: ${theme.colors.level1};
`;

const NotesContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
  color: ${theme.colors.secondary600};
  text-align: center;
`;

const ScratchpadContent = () => {
  const dispatch = useAppDispatch();
  const { currentScratchpad, loading, error, addNote, editNote, moveNote } =
    useScratchpadContext();

  const isAddExistingReportsOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.ReportsSearch, 'scratchpad'),
  );
  const isAddExistingDashboardsOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.DashboardsSearch, ''),
  );
  const isAddExistingCohortsOpen = useAppSelector((state) =>
    selectOpenedModalType(state, ModalSliceType.CohortsSearch, ''),
  );

  const [currentNoteId, setCurrentNoteId] = useState<string | null>(null);
  const editorRefs = useRef<{ [key: string]: IRichTextEditorRef }>({});
  const debouncedSaveRef = useRef<ReturnType<typeof debounce>>();
  const [activeId, setActiveId] = useState<string | null>(null);

  const currentSeq = useMemo(
    () =>
      currentScratchpad?.notes?.findIndex((note) => note.id === currentNoteId),
    [currentScratchpad?.notes, currentNoteId],
  );

  // Configure sensors for drag detection
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // pixels before dragging starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // the currently dragged note
  const activeNote = useMemo(() => {
    if (!activeId || !currentScratchpad?.notes) {
      return;
    }
    return currentScratchpad.notes.find((note) => note.id === activeId);
  }, [activeId, currentScratchpad?.notes]);

  const [localNotes, setLocalNotes] = useState<IScratchPadNoteFragment[]>([]);

  // Update local notes whenever currentScratchpad changes
  useEffect(() => {
    if (currentScratchpad?.notes) {
      setLocalNotes(currentScratchpad.notes);
    }
  }, [currentScratchpad?.notes]);

  const scratchpadId = currentScratchpad?.id;

  const {
    imageEmbedAddUploadHandle,
    startImageEmbedEditUpload,
    createImageEmbedUploadConfig,
  } = useScratchpadImageEmbed(scratchpadId || '');

  if (isNil(scratchpadId)) {
    return null;
  }

  if (loading) {
    return <EmptyState>Loading...</EmptyState>;
  }

  if (error) {
    return <EmptyState>Error loading scratchpad</EmptyState>;
  }

  if (isNil(currentScratchpad) || isNil(currentScratchpad.notes)) {
    return <EmptyState>No notes found</EmptyState>;
  }

  const handleFocus = (noteId: string | null) => {
    if (currentNoteId !== noteId) {
      debouncedSaveRef.current?.cancel();
      setCurrentNoteId(noteId);
    }
  };

  const handleContentChange = async (content: string, noteId?: string) => {
    if (!noteId) {
      return;
    }

    if (!debouncedSaveRef.current) {
      debouncedSaveRef.current = debounce(
        async (content: string, noteId: string, scratchpadId) => {
          await editNote({
            scratchpadId,
            noteId,
            html: content,
          });
        },
        1000,
      );
    }

    await debouncedSaveRef.current(content, noteId, scratchpadId);
  };

  const handleAddReport = async (
    analysis: IAnalysisSummaryInfoFieldsFragment,
    split?: ContentSplitResult,
  ) => {
    let seq = !isNil(currentSeq) ? currentSeq + 1 : null;

    if (split?.beforeHtml && currentNoteId) {
      await editNote({
        scratchpadId,
        noteId: currentNoteId,
        html: split.beforeHtml,
      });
    }

    await addNote({
      scratchpadId,
      noteType: ScratchpadNoteType.Report,
      report: {
        analysisId: analysis.id,
      },
      richText: null,
      imageEmbed: null,
      cohort: null,
      dashboard: null,
      seq: !isNil(seq) ? seq + 1 : null,
    });

    if (split?.afterHtml) {
      await addNote({
        scratchpadId,
        noteType: ScratchpadNoteType.Text,
        richText: {
          html: split.afterHtml,
        },
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        report: null,
        seq: !isNil(seq) ? seq + 2 : null,
      });
    }
  };

  const handleAddDashboard = async (
    dashboardId: string,
    split?: ContentSplitResult,
  ) => {
    let seq = !isNil(currentSeq) ? currentSeq + 1 : null;

    if (split?.beforeHtml && currentNoteId) {
      await editNote({
        scratchpadId,
        noteId: currentNoteId,
        html: split.beforeHtml,
      });
    }

    await addNote({
      scratchpadId,
      noteType: ScratchpadNoteType.Dashboard,
      dashboard: { baseId: dashboardId },
      richText: null,
      imageEmbed: null,
      cohort: null,
      report: null,
      seq: !isNil(seq) ? seq + 1 : null,
    });

    if (split?.afterHtml) {
      await addNote({
        scratchpadId,
        noteType: ScratchpadNoteType.Text,
        richText: { html: split.afterHtml },
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        report: null,
        seq: !isNil(seq) ? seq + 2 : null,
      });
    }
  };

  const handleAddCohort = async (
    cohortId: string,
    split?: ContentSplitResult,
  ) => {
    let seq = !isNil(currentSeq) ? currentSeq + 1 : null;

    if (split?.beforeHtml && currentNoteId) {
      await editNote({
        scratchpadId,
        noteId: currentNoteId,
        html: split.beforeHtml,
      });
    }

    await addNote({
      scratchpadId,
      noteType: ScratchpadNoteType.Cohort,
      cohort: { cohortId },
      richText: null,
      imageEmbed: null,
      dashboard: null,
      report: null,
      seq: !isNil(seq) ? seq + 1 : null,
    });

    if (split?.afterHtml) {
      await addNote({
        scratchpadId,
        noteType: ScratchpadNoteType.Text,
        richText: { html: split.afterHtml },
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        report: null,
        seq: !isNil(seq) ? seq + 2 : null,
      });
    }
  };

  const handleImageUploadSuccess = async (
    imageEmbed: IImageEmbedFieldsFragment,
    split: MaybeNil<ContentSplitResult>,
  ) => {
    let seq = !isNil(currentSeq) ? currentSeq + 1 : null;

    if (split?.beforeHtml && currentNoteId) {
      await editNote({
        scratchpadId,
        noteId: currentNoteId,
        html: split.beforeHtml,
      });
    }

    // Add the image note
    await addNote({
      scratchpadId,
      noteType: ScratchpadNoteType.Image,
      imageEmbed: {
        imageEmbedId: imageEmbed.id,
      },
      cohort: null,
      dashboard: null,
      report: null,
      richText: null,
      seq: !isNil(seq) ? seq + 1 : null,
    });

    // Add the content that was after the cursor as a new note
    if (split?.afterHtml) {
      await addNote({
        scratchpadId,
        noteType: ScratchpadNoteType.Text,
        imageEmbed: null,
        cohort: null,
        dashboard: null,
        report: null,
        richText: {
          html: split.afterHtml,
        },
        seq: !isNil(seq) ? seq + 2 : null,
      });
    }
  };

  const handleAddContent = async (type: string, split?: ContentSplitResult) => {
    if (isNil(currentNoteId)) {
      return;
    }

    if (type === 'image') {
      startImageEmbedEditUpload(split);
    } else if (type === 'report') {
      dispatch(
        openModal({
          type: ModalSliceType.ReportsSearch,
          data: {
            onSubmit: handleAddReport,
            split,
          },
          id: 'scratchpad',
        }),
      );
    } else if (type === 'dashboard') {
      dispatch(
        openModal({
          type: ModalSliceType.DashboardsSearch,
          data: {
            onSubmit: handleAddDashboard,
            split,
          },
        }),
      );
    } else if (type === 'cohort') {
      dispatch(
        openModal({
          type: ModalSliceType.CohortsSearch,
          data: {
            onSubmit: handleAddCohort,
            split,
          },
        }),
      );
    } else {
      // Focus the editor for the current note
      setTimeout(() => {
        editorRefs.current[currentNoteId]?.focus();
      }, 0);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;

    if (!over || !localNotes || active.id === over.id) {
      return;
    }

    const oldIndex = localNotes.findIndex((note) => note.id === active.id);
    const newIndex = localNotes.findIndex((note) => note.id === over.id);

    if (oldIndex !== newIndex) {
      // Update local state immediately
      const newNotes = [...localNotes];
      const [movedNote] = newNotes.splice(oldIndex, 1);
      newNotes.splice(newIndex, 0, movedNote);
      setLocalNotes(newNotes);

      const noteId = localNotes[oldIndex].id;

      // Send the update to kobol in the background
      try {
        await moveNote(noteId, newIndex + 1);
      } catch (error) {
        // Revert to original order on error
        setLocalNotes(currentScratchpad?.notes || []);
        console.error(error);
      }
    }
  };

  return (
    <>
      <Container>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localNotes.map((note) => note.id)}
            strategy={verticalListSortingStrategy}
          >
            <NotesContainer>
              {localNotes.map((note) => (
                <SortableNote
                  key={note.id}
                  note={note}
                  currentNoteId={currentNoteId}
                  editorRefs={editorRefs}
                  handleContentChange={handleContentChange}
                  handleAddContent={handleAddContent}
                  handleFocus={handleFocus}
                />
              ))}
            </NotesContainer>
          </SortableContext>
          <DragOverlay>
            {activeNote && <DragHeader note={activeNote} />}
          </DragOverlay>
        </DndContext>
      </Container>

      <ImageUploady>
        <ImageEmbedUpload
          ref={imageEmbedAddUploadHandle}
          createImageEmbedUploadConfig={createImageEmbedUploadConfig}
          onUploadSuccess={handleImageUploadSuccess}
        />
      </ImageUploady>
      {isAddExistingReportsOpen && <ReportSearchModal type="add-report" />}
      {isAddExistingDashboardsOpen && (
        <DashboardSearchModal type="add-dashboard" />
      )}
      {isAddExistingCohortsOpen && <CohortSearchModal type="add-cohort" />}
    </>
  );
};

export default ScratchpadContent;
